import AxiosUtil from '../Utils/AxiosUtil';
import { createContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useMutation, useQueryClient } from 'react-query';
import inMemoryJWT from './inMemoryJwt';
import AuthResponseModel from '../Interfaces/Auth/AuthResponseModel';
import CurrentUserModel from '../Interfaces/Auth/CurrentUserModel';
import RunningUserModel from '../Interfaces/Auth/RunningUserModel';
import AuthContextModel from '../Interfaces/Auth/AuthContextModel';
import AuthenticateUserUsingCurrentTokenRequest from '../Interfaces/Requests/Users/AuthenticateUserUsingCurrentToken';
import GetUserModelUsingIDRequest from '../Interfaces/Requests/Users/GetUserModelUsingIDRequest';
import GetUserModelUsingIDResponse from '../Interfaces/Responses/Users/GetUserModelUsingIDResponse';
import GenericStringObject from '../Interfaces/GenericStringObject';
import { Alert as ServaAlert, Spin } from '@servahealth/serva-ui-kit';
import Users from '../Constants/Endpoints/Users';
import { useNavigate } from 'react-router-dom';
import registerAuthInterceptors from './registerAuthInterceptors';

const localStorageKeys = ['remember', 'username', 'email'];

interface AuthVariablesType {
    callback?: (...rest: unknown[]) => void;
    onError?: (...rest: unknown[]) => void;
}

interface Props {
    children: JSX.Element;
}

export const AuthContext = createContext<AuthContextModel>({
    isAuthenticated: false,
    setIsAuthenticated: function (): void {},
    login: function (): void {},
    impersonate: function (): void {},
    logout: async function (): Promise<void> {},
    user: undefined,
    setUser: function (): void {},
    onLoginSuccess: function (): void {},
    onImpersonateSelected: function (): void {},
    currentToken: '',
    refreshToken: '',
    refreshAuthFromToken: function (): void {},
    hash: undefined,
    setHash: function (): void {},
    lastRedirectToAuth: '',
    setLastRedirectToAuth: function (): void {},
});

const AuthenticationContextProvider = (props: Props) => {
    const initialRoute = '/home';
    const nav = useNavigate();
    const [lastRedirectToAuth, setLastRedirectToAuth] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState<CurrentUserModel>();
    const [cookies, setCookie, removeCookie] = useCookies([
        'currentToken',
        'sHSSOChallenge',
    ]);

    const queryClient = useQueryClient();
    const isAuthenticatedRef = useRef(isAuthenticated);
    const refreshTokenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        isAuthenticatedRef.current = isAuthenticated;
    }, [isAuthenticated]);

    const { mutate: authenticate } = useMutation(
        async (props: AuthVariablesType) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Users.AUTHENTICATE_USERS_USING_CURRENT_TOKEN}`;
                const requestData: AuthenticateUserUsingCurrentTokenRequest = {
                    authenticationType: 2,
                    currentToken: cookies['currentToken'],
                };
                const responseData = await AxiosUtil.post<any, AuthenticateUserUsingCurrentTokenRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('AUTHENTICATE_USERS_USING_CURRENT_TOKEN: [Authenticate User] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onSuccess: (response: any) => {
                const user: AuthResponseModel = response;
                inMemoryJWT.setToken(user.userSecurityToken);
                setRefreshTokenCookie(user.currentToken);
            },
            onSettled: (data, error, variables: AuthVariablesType | void) => {
                if (!error && variables && variables.callback) {
                    variables.callback(data);
                } else if (!!error) {
                    variables?.onError?.(error);
                }
            },
        }
    );

    const { mutate: getUserModelByUserID, isLoading: isGetUserModelByUserIDLoading, isError: isGetUserModelByUserIDError, error: getUserModelByUserIDError, } = useMutation(
        async (impersonateUserId: string) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Users.GET_USER_MODEL_BY_USER_ID}`;
                if (!impersonateUserId) {
                    return Promise.reject(new Error('Impersonate user id is required.'));
                }

                const requestData: GetUserModelUsingIDRequest = {
                    guid_token: user?.currentToken ?? '',
                    guid_userid: impersonateUserId,
                };
                const responseData = AxiosUtil.get<GetUserModelUsingIDResponse, GetUserModelUsingIDRequest>(url, requestData);
                return responseData;
            } catch (err) {
                console.error('GET_USER_MODEL_BY_USER_ID: [Impersonate User] Internal Error: ', err);
                return Promise.reject();
            }
        },
        {
            onError: (err: any, impersonateUserId: string) => {
                console.error('GET_USER_MODEL_BY_USER_ID: [Impersonate User] Error: GetUserModelUsingID Failed Returned Empty Result.');
            },
            onSuccess: (response: GetUserModelUsingIDResponse, impersonateUserId: string) => {
                if (response !== undefined) {
                    if (user !== undefined) {
                        const nextRunningUser = {
                            ...user.runningUser,
                            id: response.id,
                            firstName: response.firstName,
                            lastName: response.lastName,
                            email: response.email,
                            currentToken: response.currentToken,
                            userDSAccesses: response.userDSAccesses,
                            defaultReportId: response.defaultReportID,
                            isSecurityAdmin: response.securityAdmin,
                            isTechTeam: response.techTeam,
                        };
                        setUser({ ...user, runningUser: nextRunningUser });
                    }
                    else {
                        console.error('GET_USER_MODEL_BY_USER_ID: [Impersonate User] Error: Current User Cannot Be Determined.');
                    }
                }
                else {
                    console.error('GET_USER_MODEL_BY_USER_ID: [Impersonate User] Error: GetUserModelUsingID Returned Empty Result.');
                }
            },
        }
    );

    const onLoginSuccess = (user: any) => {
        const searchParams = new URLSearchParams(window.location.search);
        let redirectUrl = searchParams.get('url') ?? '/home';

        if (!searchParams.get('url') && !!user.defaultReportID && (user.defaultReportID !== 0)) {
            redirectUrl += '/' + user.defaultReportID;
        }

        login(user);

        if (!!redirectUrl) {
            nav(decodeURIComponent(redirectUrl));
        } else {
            nav(initialRoute);
        }
    };

    const onImpersonateSelected = (currentToken: string, userID: string) => {
        const redirectUrl = '/home';
        
        getUserModelByUserID(userID);

        nav(initialRoute);

        if (!!redirectUrl) nav(decodeURIComponent(redirectUrl));
    };

    const setUserInfo = (userInfo: AuthResponseModel) => {
        const newCurrentUser = {
            id: userInfo.id,
            firstName: userInfo.firstName,
            lastName: userInfo.lastName,
            twoFactorRequired: userInfo.twoFactorRequired,
            twoFactorViaEmail: userInfo.twoFactorViaEmail,
            email: userInfo.email,
            currentToken: userInfo.currentToken,
            userSecurityToken: userInfo.userSecurityToken,
            expires: userInfo.expires,
            userDSAccesses: userInfo.userDSAccesses,
            defaultReportId: userInfo.defaultReportId,
            isSystemAccount: userInfo.isSystemAccount,
            isSecurityAdmin: userInfo.securityAdmin,
            isTechTeam: userInfo.techTeam,

            runningUser: {
                id: userInfo.id,
                firstName: userInfo.firstName,
                lastName: userInfo.lastName,
                email: userInfo.email,
                currentToken: userInfo.currentToken,
                userDSAccesses: userInfo.userDSAccesses,
                defaultReportId: userInfo.defaultReportId,
                isSystemAccount: userInfo.isSystemAccount,
                isSecurityAdmin: userInfo.securityAdmin,
                isTechTeam: userInfo.techTeam,
            } 
        };
        setUser(newCurrentUser);
    };

    const login = (user: AuthResponseModel) => {
        resetLocalStorage();

        const { userSecurityToken, currentToken } = user;
        inMemoryJWT.setToken(userSecurityToken);

        setUserInfo(user);
        setRefreshTokenCookie(currentToken);
        setIsAuthenticated(true);
    };

    const impersonate = (user: RunningUserModel) => {
        resetLocalStorage();
        setIsAuthenticated(true);
    };

    const logout = async (reason = '', deauthenticate = true) => {
        const currentToken = user?.currentToken ?? cookies['currentToken'];
        const jwtAuthorizationToken = inMemoryJWT.getToken();

        if (deauthenticate && currentToken && jwtAuthorizationToken) {
            const url = `${process.env.REACT_APP_API_ROOT}${Users.DEAUTHENTICATE_USER_USING_CURRENT_TOKEN}`;
            const requestData = { du_token: currentToken, };

            await AxiosUtil.get(url, requestData).catch((err) => {
                console.error('DEAUTHENTICATE_USER_USING_CURRENT_TOKEN: [Logout] Internal Error: ', err);
            });
        }

        if (refreshTokenTimeoutRef.current) {
            clearTimeout(refreshTokenTimeoutRef.current);
            refreshTokenTimeoutRef.current = null;
        }

        removeCookie('currentToken', { path: '/', domain: window.location.hostname, });
        setIsAuthenticated(false);
        setUser(undefined);
        setLastRedirectToAuth(reason);
        resetLocalStorage();
        inMemoryJWT.eraseToken();
        queryClient.removeQueries();
    };

    const logoutRef = useRef<(reason?: string, deauthenticate?: boolean) => Promise<void>>(async () => undefined);
    logoutRef.current = logout;

    useLayoutEffect(() => {
        const unregisterInterceptors = registerAuthInterceptors({
            getToken: () => inMemoryJWT.getToken() ?? undefined,
            getAllowedOrigins: () => process.env.REACT_APP_API_ROOT,
            onUnauthorized: async () => {
                if (isAuthenticatedRef.current) {
                    isAuthenticatedRef.current = false;
                    await logoutRef.current('Last Session Ended Due To: Authorization Expired.', false);
                    nav('/');
                }
            },
        });

        return unregisterInterceptors;
    }, [nav]);

    useEffect(() => {
        return () => {
            if (refreshTokenTimeoutRef.current) {
                clearTimeout(refreshTokenTimeoutRef.current);
                refreshTokenTimeoutRef.current = null;
            }
        };
    }, []);

    const resetLocalStorage = () => {
        const backup: GenericStringObject = {};
        localStorageKeys.forEach((k) => {
            const value = localStorage.getItem(k);
            if (value) backup[k] = value;
        });

        localStorage.clear();

        localStorageKeys.forEach((k) => {
            if (backup[k]) localStorage.setItem(k, backup[k]);
        });
    };

    const getNewRefreshToken = () => {
        authenticate({});
    };

    const setRefreshTokenCookie = (currentToken: string) => {
        const currDate = new Date();
        const expires = new Date();
        expires.setMinutes(new Date().getMinutes() + 10);
        const expireTimeMS = expires.getTime() - currDate.getTime() - 1000;

        setCookie('currentToken', currentToken, {
            path: '/',
            expires: expires,
            secure: true,
            domain: window.location.hostname,
        });

        if (refreshTokenTimeoutRef.current) {
            clearTimeout(refreshTokenTimeoutRef.current);
            refreshTokenTimeoutRef.current = null;
        }

        if (inMemoryJWT.getToken()) {
            refreshTokenTimeoutRef.current = setTimeout(() => {
                refreshTokenTimeoutRef.current = null;
                getNewRefreshToken();
            }, expireTimeMS);
        }
    };

    const handleHash = (value: string | undefined) => {
        const expires = new Date();
        expires.setMinutes(new Date().getMinutes() + 30);
        setCookie('sHSSOChallenge', value, {
            path: '/',
            expires,
            secure: true,
            domain: window.location.hostname,
        });
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated: isAuthenticated,
                setIsAuthenticated: setIsAuthenticated,
                login,
                impersonate,
                logout,
                user,
                setUser: setUserInfo,
                onLoginSuccess,
                onImpersonateSelected,
                currentToken: inMemoryJWT.getToken() ?? '',
                refreshToken: cookies['currentToken'],
                refreshAuthFromToken: authenticate,
                hash: cookies['sHSSOChallenge'],
                setHash: handleHash,
                lastRedirectToAuth: lastRedirectToAuth,
                setLastRedirectToAuth: setLastRedirectToAuth,
            }}
        >
            {props.children}
            {
                (isGetUserModelByUserIDLoading) &&
                (
                    <Spin></Spin>
                )
            }
            {
                (isGetUserModelByUserIDError) &&
                (
                    <ServaAlert
                        message={(getUserModelByUserIDError as Error).message}
                        type="error"
                    />
                )
            }
        </AuthContext.Provider>
    );
};

export default AuthenticationContextProvider;
